import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import * as Yup from 'yup';
import { Formik } from 'formik';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography
} from '@mui/material';
import AccountService from '../services/AccountService';
import { hide, show } from '../store/actions/busyIndicatorActions';
import { showAlert as showGlobalAlert, clearAllAlerts as clearAllGlobalAlerts } from '../store/actions/alertActions';
import { formatErrorMessage, formatSuccessMessage, getQueryParam } from '../utils/common';
import AppConstants from '../constants/AppConstants';

interface Props {
  showBusyIndicator: any,
  hideBusyIndicator: any,
  showAlert: any,
  clearAllAlerts: any
}

const ResetPassword: React.FC<Props> = ({
  showBusyIndicator,
  hideBusyIndicator,
  showAlert,
  clearAllAlerts
}) => {
  const { id = '' } = useParams();
  const token = getQueryParam('token');
  const navigate = useNavigate();

  const [isPasswordResetSuccess, setIsPasswordResetSuccess] = useState(false);

  const formInitialValues = {
    password: '',
    confirmPassword: ''
  };
  const formValidationSchema = Yup.object({
    password: Yup.string().max(255).required('Password is required'),
    confirmPassword: Yup.string().max(255).oneOf([Yup.ref('password'), ''], 'Passwords must match')
  });
  const handleFormSubmit = (values: any) => {
    showBusyIndicator();
    clearAllAlerts();
    AccountService.resetPassword(id, values.password, token)
      .then((resp) => {
        setIsPasswordResetSuccess(true);
        showAlert(formatSuccessMessage(formatSuccessMessage(resp)));
      }).catch((err) => {
        showAlert(formatErrorMessage(JSON.parse(err.response)));
      }).then(() => {
        hideBusyIndicator();
      });
  };
  const redirectToLogin = () => {
    navigate('/login');
  };

  return (
    <>
      <Helmet>
        <title>Reset Password | {AppConstants.APP_NAME}</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          justifyContent: 'center'
        }}
      >
        {
          !isPasswordResetSuccess && (
            <Container maxWidth="sm">
              <Formik
                initialValues={formInitialValues}
                validationSchema={formValidationSchema}
                onSubmit={handleFormSubmit}
              >
                {({
                  errors,
                  handleBlur,
                  handleChange,
                  handleSubmit,
                  touched,
                  values
                }) => (
                  <form onSubmit={handleSubmit}>
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        color="textPrimary"
                        variant="h2"
                      >
                        Reset Password
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        pb: 1,
                        pt: 3
                      }}
                    >
                      <Typography
                        align="center"
                        color="textSecondary"
                        variant="body1"
                      >
                        Enter your new password
                      </Typography>
                    </Box>
                    <TextField
                      error={Boolean(touched.password && errors.password)}
                      fullWidth
                      helperText={touched.password && errors.password}
                      label="Password"
                      margin="normal"
                      name="password"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      type="password"
                      value={values.password}
                      variant="outlined"
                    />
                    <TextField
                      error={Boolean(touched.confirmPassword && errors.confirmPassword)}
                      fullWidth
                      helperText={touched.confirmPassword && errors.confirmPassword}
                      label="Confirm Password"
                      margin="normal"
                      name="confirmPassword"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      type="password"
                      value={values.confirmPassword}
                      variant="outlined"
                    />
                    <Box sx={{ py: 2 }}>
                      <Button
                        color="primary"
                        fullWidth
                        size="large"
                        type="submit"
                        variant="contained"
                      >
                        Reset
                      </Button>
                    </Box>
                  </form>
                )}
              </Formik>
            </Container>
          )
        }

        {
          isPasswordResetSuccess && (
            <Container maxWidth="sm">
              <Box sx={{ mb: 3 }}>
                <Typography
                  align="center"
                  color="textPrimary"
                  variant="h2"
                >
                  Reset Password Successful
                </Typography>
              </Box>
              <Box
                sx={{
                  pb: 1,
                  pt: 3
                }}
              >
                <Typography
                  align="center"
                  color="textSecondary"
                  variant="body1"
                >
                  Please login with your new password
                </Typography>
              </Box>
              <Box sx={{ py: 2 }}>
                <Button
                  color="primary"
                  fullWidth
                  size="large"
                  type="submit"
                  variant="contained"
                  onClick={redirectToLogin}
                >
                  Sign in
                </Button>
              </Box>
            </Container>
          )
        }
      </Box>
    </>
  );
};

const mapStateToProps = null;

const mapDispatchToProps = (dispatch: any) => ({
  showBusyIndicator: () => dispatch(show()),
  hideBusyIndicator: () => dispatch(hide()),
  showAlert: (message: any) => dispatch(showGlobalAlert(message)),
  clearAllAlerts: () => dispatch(clearAllGlobalAlerts())
});

export default connect(mapStateToProps, mapDispatchToProps)(ResetPassword);
